import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Send, ArrowLeft, MessageSquare, Trash2, Info } from "lucide-react";

export default function Sugerencias() {
  const navigate = useNavigate();
  const [texto, setTexto] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (texto.trim().length < 3) {
      toast.error("Escribe al menos 3 caracteres.");
      return;
    }
    setSending(true);
    try {
      await api.post("/sugerencias", { texto: texto.trim() });
      setSent(true);
      setTexto("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la sugerencia.");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-5 pb-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-slate-500 text-xs font-semibold hover:text-[#FF4500]"
      >
        <ArrowLeft size={14} /> Volver
      </button>
      <header>
        <div className="label-up text-[#FF4500]">Buzón anónimo</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1 flex items-center gap-2">
          <MessageSquare size={26} /> Sugerencias
        </h1>
        <p className="text-xs text-slate-500 mt-2">
          Cuéntanos cualquier propuesta, mejora o idea. Las sugerencias son <b>anónimas</b>:
          no se guarda quién las ha enviado.
        </p>
      </header>

      {sent ? (
        <div
          className="brutal-card p-6 space-y-3 text-center border-emerald-300 bg-emerald-50"
          data-testid="sugerencia-thanks"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600 flex items-center justify-center">
            <Send size={22} className="text-white" />
          </div>
          <h2 className="font-display font-black text-xl text-emerald-800">¡Gracias!</h2>
          <p className="text-sm text-emerald-700">
            Tu sugerencia se ha registrado correctamente.
          </p>
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setSent(false)}
              data-testid="sugerencia-another"
              className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-white"
            >
              Enviar otra
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex-1 h-11 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      ) : (
        <div className="brutal-card p-4 space-y-3">
          <textarea
            data-testid="sugerencia-textarea"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            maxLength={5000}
            className="brutal-input w-full min-h-[180px] resize-y"
            placeholder="Escribe aquí tu sugerencia, propuesta o idea..."
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1"><Info size={12} /> Anónimo · sin nombre ni email</span>
            <span>{texto.length}/5000</span>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={sending || texto.trim().length < 3}
            data-testid="sugerencia-submit"
            className="brutal-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Enviar sugerencia
          </button>
        </div>
      )}
    </div>
  );
}


export function AdminSugerencias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "sadmin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sugerencias");
      setItems(data || []);
    } catch {
      toast.error("No se pudieron cargar las sugerencias");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta sugerencia?")) return;
    try {
      await api.delete(`/admin/sugerencias/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Sugerencia eliminada");
    } catch { toast.error("No se pudo eliminar"); }
  };

  if (!isAdmin) {
    return <div className="text-center text-slate-500 py-10">Sin permiso.</div>;
  }

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="label-up text-[#FF4500]">Buzón</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-1">Sugerencias</h1>
          <p className="text-xs text-slate-500 mt-1">
            Las sugerencias son anónimas — solo se muestra el texto y la fecha.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-slate-500 py-10">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="brutal-card p-8 text-center text-slate-500">
          Aún no hay sugerencias registradas.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((s) => (
            <li key={s.id} className="brutal-card p-3" data-testid={`sug-row-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                  <MessageSquare size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{s.texto}</div>
                  <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
                    <span>{new Date(s.created_at).toLocaleString("es-ES")}</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-bold text-[10px] ${
                      s.email_sent ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {s.email_sent ? "Enviado por email" : "Solo en BD"}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => remove(s.id)}
                  className="w-9 h-9 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  data-testid={`sug-delete-${s.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
